import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdLocationOn, MdSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import { fetchDistributorVenues } from '../../../../services/Http/DistributorApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const DistributorVenues = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const loadVenues = useCallback(async ({ page = 1, perPage = 10, searchTerm = '' } = {}) => {
        setLoading(true);
        try {
            const response = await fetchDistributorVenues({ page, perPage, search: searchTerm });
            setRecords(response.data || []);
            setMeta(response.meta || { total: 0, per_page: perPage, current_page: page });
        } catch (error) {
            toast.error(t('DistributorVenues.errors.loadList'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadVenues();
    }, [loadVenues]);

    const handleSearch = (event) => {
        event.preventDefault();
        loadVenues({ page: 1, perPage: meta.per_page, searchTerm: search });
    };

    const columns = [
        {
            name: t('DistributorVenues.table.columns.venue'),
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: t('DistributorVenues.table.columns.retailer'),
            selector: (row) => row.retailer || '—',
            sortable: true,
        },
        {
            name: t('DistributorVenues.table.columns.location'),
            selector: (row) => `${row.city || ''}${row.country ? `, ${row.country}` : ''}`,
            sortable: true,
        },
        {
            name: t('DistributorVenues.table.columns.outboundValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.outbound_value,
                localAmount: row.outbound_value_local ?? row.outbound_local,
                localCurrencyId: row.seller_currency_id ?? row.currency_id,
                localCurrencyCode: row.seller_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('DistributorVenues.table.columns.lastActivity'),
            selector: (row) => formatDate(row.last_transaction_at),
            sortable: true,
        },
        {
            name: t('DistributorVenues.table.columns.action'),
            cell: (row) => (
                <ActionButton onClick={() => navigate(`/venues/${row.id}`)}>
                    {t('DistributorVenues.table.view')}
                </ActionButton>
            ),
        },
    ];

    return (
        <PageContainer>
            <PageHeader>
                <TitleBlock>
                    <PageTitle>{t('DistributorVenues.title')}</PageTitle>
                    <Subtitle>{t('DistributorVenues.subtitle')}</Subtitle>
                </TitleBlock>
                <IconBadge>
                    <MdLocationOn size={28} />
                </IconBadge>
            </PageHeader>
            <PageContent>
                <Toolbar>
                    <SearchForm onSubmit={handleSearch}>
                        <MdSearch />
                        <input
                            type="text"
                            placeholder={t('DistributorVenues.table.search')}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </SearchForm>
                </Toolbar>
                <DataTable
                    columns={columns}
                    data={records}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={meta.total}
                    paginationPerPage={meta.per_page}
                    onChangePage={(page) => loadVenues({ page, perPage: meta.per_page, searchTerm: search })}
                    onChangeRowsPerPage={(perPage, page) => loadVenues({ page, perPage, searchTerm: search })}
                    highlightOnHover
                    persistTableHead
                />
            </PageContent>
        </PageContainer>
    );
};

export default DistributorVenues;

const formatDate = (value) => {
    if (!value) {
        return '—';
    }
    return new Date(value).toLocaleString();
};

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const IconBadge = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(130, 22, 146, 0.1);
    display: flex;
    align_items: center;
    justify-content: center;
    color: rgba(130, 22, 146, 1);
`;

const Toolbar = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 1rem;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 0.05);
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        background: transparent;
        outline: none;
        width: 220px;
    }

    svg {
        color: rgba(130, 22, 146, 1);
    }
`;

const ActionButton = styled.button`
    padding: 0.5rem 0.875rem;
    border-radius: 6px;
    border: none;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    cursor: pointer;
`;
